import { DOCS_URL } from '@/shared/constants/docs';
import { useAppDispatch, useAppSelector } from '@/shared/hooks/useStore';
import { useTrans } from '@/shared/hooks/useTranslation';
import { formatDisplayVersion } from '@/shared/utils';
import { useOpenAnnouncements } from '@/viewmodel/shared/versionCheck/hooks/useOpenAnnouncements';
import { useSignedInUserId } from '@/viewmodel/shared/versionCheck/hooks/useSignedInUserId';
import { toAnnouncementNotes } from '@/viewmodel/shared/versionCheck/mappers/releaseNotes';
import {
  panelClosed,
  selectAnnouncementPanelOpen,
  selectCurrentAnnouncements,
  selectHasUnseenAnnouncements,
} from '@/viewmodel/shared/versionCheck/state/versionCheckSlice';
import type { WhatsNewViewState } from '@/viewmodel/shared/versionCheck/types';

function changelogUrl(language: string): string {
  return language.startsWith('es') ? `${DOCS_URL}/es/changelog/` : `${DOCS_URL}/changelog/`;
}

export function useWhatsNewViewModel(): WhatsNewViewState | null {
  const { i18n } = useTrans();
  const dispatch = useAppDispatch();
  const current = useAppSelector(selectCurrentAnnouncements);
  const hasUnseen = useAppSelector(selectHasUnseenAnnouncements);
  const isOpen = useAppSelector(selectAnnouncementPanelOpen);
  const openCurrent = useOpenAnnouncements(useSignedInUserId());

  if (!current) return null;

  const close = (): void => {
    dispatch(panelClosed());
  };

  return {
    version: formatDisplayVersion(current.version),
    notes: toAnnouncementNotes(current.notes, i18n.language),
    hasUnseen,
    isOpen,
    changelogUrl: changelogUrl(i18n.language),
    setOpen: (open) => (open ? openCurrent(current) : close()),
    close,
  };
}
